import { supabase } from '@/lib/supabase';
import { RespuestasEncuesta } from '../components/EncuestaSatisfaccion';

const LOCAL_KEY = 'upal_encuestas_satisfaccion';

export interface EncuestaRecord {
  id?: string;
  fecha_registro: string;
  email_usuario: string;
  p1: number;
  p2: number;
  p3: number;
  p4: number;
  p5: number;
  p6: number;
  p7: number;
  p8: number;
  p9: number;
  p10: number;
  /** Puntuación SUS calculada (0-100) */
  puntaje_sus: number;
  comentario: string;
}

/**
 * Calcula el puntaje SUS estándar (0-100).
 * Preguntas impares (positivas): (valor - 1) * 2.5
 * Preguntas pares (negativas): (5 - valor) * 2.5
 * Suma total de los 10 ítems.
 */
export const calcularPuntajeSUS = (r: RespuestasEncuesta): number => {
  const positivas = [r.p1, r.p3, r.p5, r.p7, r.p9];
  const negativas = [r.p2, r.p4, r.p6, r.p8, r.p10];
  const sumaPos = positivas.reduce((acc, v) => acc + (v - 1), 0);
  const sumaNeg = negativas.reduce((acc, v) => acc + (5 - v), 0);
  return Math.round((sumaPos + sumaNeg) * 2.5);
};

export const interpretarSUS = (puntaje: number): string => {
  if (puntaje >= 85) return 'Excelente';
  if (puntaje >= 71) return 'Bueno';
  if (puntaje >= 51) return 'Aceptable';
  if (puntaje >= 35) return 'Deficiente';
  return 'Inaceptable';
};

// ── Helpers localStorage ──────────────────────────────────────────────────────

const getLocalEncuestas = (): EncuestaRecord[] => {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as EncuestaRecord[];
  } catch {
    return [];
  }
};

const saveLocalEncuesta = (record: EncuestaRecord): void => {
  const existing = getLocalEncuestas();
  const updated = [record, ...existing];
  localStorage.setItem(LOCAL_KEY, JSON.stringify(updated));
};

// ── Detectar si Supabase está disponible ─────────────────────────────────────

const isSupabaseAvailable = (): boolean => {
  try {
    const url = (supabase as unknown as { supabaseUrl?: string }).supabaseUrl;
    return typeof url === 'string' && url.length > 0 && !url.includes('placeholder');
  } catch {
    return false;
  }
};

// ── API pública ───────────────────────────────────────────────────────────────

export const saveEncuesta = async (
  emailUsuario: string,
  respuestas: RespuestasEncuesta
): Promise<{ ok: boolean; error?: string }> => {
  const puntaje = calcularPuntajeSUS(respuestas);

  const record: EncuestaRecord = {
    id: crypto.randomUUID(),
    fecha_registro: new Date().toISOString(),
    email_usuario: emailUsuario,
    p1: respuestas.p1,
    p2: respuestas.p2,
    p3: respuestas.p3,
    p4: respuestas.p4,
    p5: respuestas.p5,
    p6: respuestas.p6,
    p7: respuestas.p7,
    p8: respuestas.p8,
    p9: respuestas.p9,
    p10: respuestas.p10,
    puntaje_sus: puntaje,
    comentario: respuestas.comentario,
  };

  // Intentar Supabase primero si está disponible
  if (isSupabaseAvailable()) {
    try {
      const { error } = await supabase.from('encuestas_satisfaccion').insert([record]);
      if (!error) {
        console.log('[Encuesta] Guardada en Supabase correctamente');
        return { ok: true };
      }
      console.warn('[Encuesta] Supabase falló, usando localStorage:', error.message);
    } catch (err) {
      console.warn('[Encuesta] Supabase no disponible, usando localStorage:', err);
    }
  }

  // Fallback: guardar en localStorage
  saveLocalEncuesta(record);
  console.log('[Encuesta] Guardada en localStorage correctamente');
  return { ok: true };
};

export const getEncuestas = async (): Promise<EncuestaRecord[]> => {
  // Intentar Supabase primero si está disponible
  if (isSupabaseAvailable()) {
    try {
      const { data, error } = await supabase
        .from('encuestas_satisfaccion')
        .select('*')
        .order('fecha_registro', { ascending: false });

      if (!error && data) {
        // Combinar con localStorage por si hay registros locales también
        const localData = getLocalEncuestas();
        const combined = [...(data as EncuestaRecord[]), ...localData];
        // Deduplicar por id
        const seen = new Set<string>();
        return combined.filter((r) => {
          const key = r.id ?? r.fecha_registro + r.email_usuario;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }
      console.warn('[Encuesta] Supabase falló al obtener, usando localStorage:', error?.message);
    } catch (err) {
      console.warn('[Encuesta] Supabase no disponible, usando localStorage:', err);
    }
  }

  // Fallback: leer desde localStorage
  return getLocalEncuestas();
};
